import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
    Compass, 
    Database,
    Zap,
    SlidersHorizontal,
    Bell,
    LogOut,
    Menu,
    X,
    UserCircle,
    ChevronDown,
    TrendingUp,
    LayoutDashboard
} from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../services/AuthContext';
import { useCountry } from '../context/CountryContext';
import { useTranslation } from 'react-i18next';

interface DeveloperTerminalLayoutProps {
    children: ReactNode;
}

const DeveloperTerminalLayout = ({ children }: DeveloperTerminalLayoutProps) => {
    const { t, i18n } = useTranslation();
    const { user, logout } = useAuth();
    const { activeCountry } = useCountry();
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isNotificationsMenuOpen, setIsNotificationsMenuOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
    }, [location.pathname]);

    const getPath = (path: string) => {
        const prefix = activeCountry ? `/${activeCountry.code.toLowerCase()}` : '';
        return `${prefix}${path}`;
    };

    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        i18n.changeLanguage(newLang);
    };

    const navigation = useMemo(() => [
        { name: t('nav_home'), href: getPath('/developer/dashboard'), icon: Compass },
        { name: t('nav_inventory_hub'), href: getPath('/developer/inventory'), icon: Database },
        { name: t('nav_sales'), href: getPath('/developer/sales'), icon: LayoutDashboard },
        { name: t('nav_insights'), href: getPath('/developer/insights'), icon: TrendingUp },
        { name: t('nav_settings'), href: getPath('/developer/settings'), icon: SlidersHorizontal },
    ], [activeCountry, t]);



    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    const renderNavItem = (item: any) => {
        return (
            <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) => `
                    flex items-center group relative
                    ${isSidebarCollapsed 
                        ? `w-10 h-10 justify-center rounded-md mb-1 transition-none ${isActive ? 'bg-white/10 text-white' : 'text-[#94A3B8] hover:bg-white/5 hover:text-white'}` 
                        : `w-full gap-3 py-3 px-4 rounded-md transition-all duration-200 ${isActive ? 'bg-navy-600/80 text-white shadow-sm ring-1 ring-emerald-500/30' : 'text-[#94A3B8] hover:bg-navy-600/50 hover:text-white'}`
                    }
                `}
            >
                {({ isActive }) => (
                    <>
                        {isSidebarCollapsed && (
                            <div className="absolute start-[calc(100%+8px)] px-3 py-2 bg-navy text-white text-sm font-bold rounded-[4px] opacity-0 group-hover:opacity-100 transition-all duration-100 pointer-events-none z-[100] whitespace-nowrap shadow-2xl border border-white/5 flex items-center min-w-[80px]">
                                {item.name}
                            </div>
                        )}
                        <item.icon 
                            className={`w-5 h-5 shrink-0 transition-all duration-200 ${isActive ? 'opacity-100' : 'opacity-60 group-hover:opacity-100'}`} 
                            strokeWidth={1.5} 
                        />
                        {!isSidebarCollapsed && (
                            <span className="text-sm font-medium whitespace-nowrap overflow-hidden">
                                {item.name}
                            </span>
                        )}
                    </>
                )}
            </NavLink>
        );
    };

    return (
        <div className="fixed inset-0 bg-navy dark:bg-black flex overflow-hidden overscroll-none selection:bg-accent-blue/30 font-sans">
            {/* --- Desktop Sidebar --- */}
            <aside className={`${isSidebarCollapsed ? 'w-[60px] overflow-visible' : 'w-[220px] overflow-hidden'} transition-[width] duration-300 flex-shrink-0 h-full bg-navy dark:bg-black text-white hidden lg:flex flex-col z-50`}>
                <div className={`${isSidebarCollapsed ? 'p-3 flex flex-col items-center' : 'p-6'}`}>
                    <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center mb-6' : 'gap-3 px-2 mb-10'} cursor-pointer overflow-hidden min-h-[24px]`} onClick={() => navigate(getPath('/developer/dashboard'))}>
                        {isSidebarCollapsed ? (
                            <img src="/laqyicon.svg" alt="LAQY" className="h-6 w-auto flex-shrink-0" />
                        ) : (
                            <img src="/laqy-developers-logo.svg" alt="LAQY Terminal" className="h-8 w-auto flex-shrink-0 ms-1" />
                        )}
                    </div>
                    
                    <nav className={`space-y-1 no-scrollbar max-h-[calc(100vh-250px)] overscroll-contain ${isSidebarCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
                        {navigation.map((item) => renderNavItem(item))}
                    </nav>
                </div>

                <div className={`mt-auto border-t border-white/5 space-y-2 ${isSidebarCollapsed ? 'p-2' : 'p-4'}`}>
                    {!isSidebarCollapsed ? (
                        <>
                            <div className="flex items-center gap-3 px-2 py-2">
                                <div className="w-8 h-8 rounded-full bg-navy-600 flex items-center justify-center flex-shrink-0 font-bold text-xs shadow-sm">
                                     {user?.first_name?.[0] || user?.email?.[0] || 'D'}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold truncate leading-tight">{user?.first_name || 'Member'}</p>
                                    <p className="text-[10px] text-[#94A3B8] font-medium mt-0.5">Developer Account</p>
                                </div>
                            </div>
                            
                            <button 
                                onClick={logout}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-[#94A3B8] hover:text-white hover:bg-white/5 transition-all text-sm font-medium whitespace-nowrap"
                            >
                                <LogOut size={16} className="flex-shrink-0" />
                                Logout
                            </button>
                        </>
                    ) : (
                        <div className="flex flex-col items-center gap-4 py-2">
                            <div className="w-8 h-8 rounded-full bg-navy-600 flex items-center justify-center font-bold text-xs shadow-sm">
                                {user?.first_name?.[0] || user?.email?.[0] || 'D'}
                            </div>
                            <button 
                                onClick={logout}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-[#94A3B8] hover:text-white hover:bg-white/5 transition-all relative group"
                            >
                                <div className="absolute start-[calc(100%+8px)] px-3 py-2 bg-navy text-white text-sm font-bold rounded-[4px] opacity-0 group-hover:opacity-100 transition-all duration-100 pointer-events-none z-[100] whitespace-nowrap shadow-2xl border border-white/5 flex items-center min-w-[80px]">
                                    Logout
                                </div>
                                <LogOut size={16} />
                            </button>
                        </div>
                    )}
                    
                    <div className="flex px-4 py-2">
                        <button 
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            className={`w-8 h-8 rounded-md bg-black/20 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer ${isSidebarCollapsed ? 'mx-auto' : 'ms-auto'}`}
                        >
                            <ChevronDown size={14} className={`transition-transform duration-300 ${isSidebarCollapsed ? '-rotate-90' : 'rotate-90'}`} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* --- Main Content Area --- */}
            <main className="flex-1 flex flex-col h-full min-w-0 bg-navy dark:bg-black relative">
                {/* Top Header */}
                <header className="h-[48px] flex-shrink-0 bg-transparent px-6 lg:px-8 flex items-center justify-between lg:justify-end z-40 text-white">
                    <div className="flex items-center gap-4 lg:hidden">
                        <button 
                            onClick={toggleMobileMenu}
                            className="p-2 -ms-2 text-white/80 hover:text-white transition-colors"
                        >
                            <Menu size={24} />
                        </button>
                        <img src="/laqysymbolandlogo_premium.svg" alt="LAQY" className="h-5 w-auto brightness-0 invert" />
                    </div>

                    <div className="flex items-center gap-4 text-white relative">
                        {/* Global Network Latency Indicator - Only on Desktop */}
                        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/5">
                            <Zap size={10} className="text-accent-blue-400 fill-accent-blue-400/20" />
                            <span className="text-[10px] font-mono font-bold text-accent-blue-400">23ms</span>
                        </div>

                        <div className="flex items-center gap-3 ps-2">
                            <button 
                                onClick={toggleLanguage}
                                className="text-sm font-bold text-white/80 transition-none"
                            >
                                {i18n.language === 'en' ? 'عربي' : 'English'}
                            </button>

                            {/* Notifications */}
                            <div className="relative">
                                <button 
                                    onClick={() => {
                                        setIsNotificationsMenuOpen(!isNotificationsMenuOpen);
                                        setIsProfileMenuOpen(false);
                                    }}
                                    className="relative w-8 h-8 flex items-center justify-center text-white/80 hover:bg-white/10 rounded-full transition-colors"
                                >
                                    <Bell size={18} strokeWidth={1.5} />
                                    <span className="absolute top-1.5 end-1.5 w-2 h-2 bg-terracotta-500 rounded-full border-2 border-navy"></span>
                                </button>

                                {isNotificationsMenuOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsNotificationsMenuOpen(false)}></div>
                                        <div className="absolute end-0 top-full mt-2 w-80 bg-white dark:bg-[#111] rounded-md shadow-2xl border border-slate-200 dark:border-white/5 z-50 overflow-hidden text-slate-900 dark:text-white">
                                            <div className="p-4 border-b border-slate-200 dark:border-white/5 flex justify-between items-center">
                                                <h3 className="font-semibold text-[11px] text-slate-400">Notifications</h3>
                                                <span className="text-[9px] px-2 py-0.5 bg-accent-blue-500/10 text-accent-blue-400 rounded-full font-bold tabular-nums">0 new</span>
                                            </div>
                                            <div className="p-10 flex flex-col items-center justify-center text-center">
                                                <div className="w-10 h-10 bg-slate-50 dark:bg-white/5 rounded-full flex items-center justify-center mb-3">
                                                    <Bell size={18} className="text-slate-300" />
                                                </div>
                                                <p className="text-slate-400 text-[11px] font-medium">Clear stack. No signals.</p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            
                            {/* Profile Dropdown */}
                            <div className="relative">
                                <div 
                                    onClick={() => {
                                        setIsProfileMenuOpen(!isProfileMenuOpen);
                                        setIsNotificationsMenuOpen(false);
                                    }}
                                    className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center overflow-hidden border border-white/10 cursor-pointer shadow-lg"
                                >
                                    <UserCircle size={20} className="text-white/80" />
                                </div>

                                {isProfileMenuOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsProfileMenuOpen(false)}></div>
                                        <div className="absolute end-0 top-full mt-2 w-72 bg-white dark:bg-[#111] rounded-md shadow-2xl border border-slate-200 dark:border-white/5 z-50 overflow-hidden text-slate-900 dark:text-white">
                                            <div className="p-5 border-b border-slate-200 dark:border-white/10 flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-full bg-accent-blue-600 text-white flex items-center justify-center text-xl font-black shrink-0 shadow-lg shadow-accent-blue-500/10">
                                                    {user?.first_name?.[0] || user?.email?.[0] || 'D'}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="font-bold text-xs text-slate-900 dark:text-white truncate">
                                                        {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : 'Corporate Member'}
                                                    </h3>
                                                    <p className="text-[10px] text-slate-500 truncate mt-1 font-medium">
                                                        {user?.email}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="p-3">
                                                <button 
                                                    onClick={() => {
                                                        setIsProfileMenuOpen(false);
                                                        navigate(getPath('/developer/settings'));
                                                    }}
                                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-[10px] font-black text-slate-700 dark:text-slate-400 tracking-tight hover:text-accent-blue-600 hover:bg-slate-50 dark:hover:bg-white/5 rounded-md transition-all"
                                                >
                                                    <SlidersHorizontal size={16} strokeWidth={1.5} />
                                                    Corporate Settings
                                                </button>
                                            </div>

                                            <div className="p-3 border-t border-slate-200 dark:border-white/10">
                                                <button 
                                                    onClick={() => {
                                                        setIsProfileMenuOpen(false);
                                                        logout();
                                                    }}
                                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-[10px] font-black text-red-500 tracking-tight hover:bg-red-500/5 rounded-md transition-all"
                                                >
                                                    <LogOut size={16} strokeWidth={1.5} />
                                                    Sign Out
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content Container - The carded container matching Partners */}
                <div className="flex-1 bg-white dark:bg-[#0A0A0A] rounded-t-lg shadow-2xl shadow-black/10 relative flex flex-col overflow-hidden mt-1 ms-1">
                    <div 
                        ref={scrollContainerRef}
                        className="flex-1 overflow-y-auto custom-scrollbar overscroll-contain"
                    >
                        <div className="p-6 md:p-8 lg:p-10 w-full max-w-[1600px] mx-auto min-h-full">
                            {children}
                        </div>
                    </div>
                </div>
            </main>

            {/* --- Mobile Drawer --- */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-[100] lg:hidden">
                    <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" onClick={toggleMobileMenu}></div>
                    <aside className="absolute inset-y-0 start-0 w-72 bg-navy text-white flex flex-col p-6 transition-all duration-300">
                        <div className="flex items-center justify-between mb-10 px-2">
                             <div className="flex items-center gap-2" onClick={() => { navigate(getPath('/developer/dashboard')); toggleMobileMenu(); }}>
                                <img src="/laqysymbolandlogo_premium.svg" alt="LAQY" className="h-5 brightness-0 invert" />
                             </div>
                            <button onClick={toggleMobileMenu} className="p-2 -me-2 text-white/70">
                                <X size={24} />
                            </button>
                        </div>

                        <nav className="space-y-1 overflow-y-auto no-scrollbar">
                            {navigation.map((item) => renderNavItem(item))}
                        </nav>
                        
                        <div className="mt-auto pt-8 border-t border-white/5">
                             <div className="flex items-center gap-3 px-3 py-3 bg-white/5 rounded-md border border-white/5">
                                <div className="w-10 h-10 rounded-md bg-accent-blue-600 flex items-center justify-center flex-shrink-0 font-black text-md shadow-lg shadow-accent-blue-500/20">
                                     {user?.first_name?.[0] || user?.email?.[0] || 'D'}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-bold truncate leading-tight">{user?.first_name || 'Member'}</p>
                                    <p className="text-[10px] text-accent-blue-400 font-medium mt-1">Developer Account</p>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
};

export default DeveloperTerminalLayout;
